import { useCallback, useEffect, useRef, useState } from 'react';

import {
  EditorState,
  Modifier,
  RichUtils,
  SelectionState,
  getDefaultKeyBinding,
} from 'draft-js';
import {
  changeDepth,
  getEntityRange,
  getSelectedBlocksMetadata,
  getSelectedBlocksType,
  getSelectionCustomInlineStyle,
  getSelectionEntity,
  getSelectionInlineStyle,
  removeAllInlineStyles,
  setBlockData,
  toggleCustomInlineStyle,
} from 'draftjs-utils';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import {
  commitTaskImage,
  compressImageForUpload,
  getLoggedUserFromToken,
  resolveTaskImageUrl,
  uploadTaskImageStaged,
  validateEditorSession,
} from '../../api/api-functions';
import { setAuthorizationToken } from '../../utilities/api-helper';
import { isIOSDevice } from '../../utilities/platform-helper';
import { mobileEditorCss } from './editor-css';
import { getCaretRangeAtPoint, getNearestCaretRangeInBlock } from './ios-caret';
import {
  applyResolvedImageSrc,
  attachImageFallback,
  collectDescriptionImageKeys,
  collectImageEntitySrcs,
  createEditorStateFromHtml,
  createPastedTextHandler,
  dedupeFontStyles,
  fontFamilyStyleMap,
  getContentHtml,
  hydratePastedImages,
  insertImageBlock,
  insertVideoBlock,
  ensureTrailingTextBlock,
  FONT_FAMILY_STACKS,
  persistDescriptionImages,
  placeCaretAfterBlock,
  removeAtomicBlock,
  replaceImageEntitySrc,
  richTextEditorToolbar,
} from '../../pure-ui/RichTextEditor/editor-utils';

const postNativeMessage = (payload) => {
  const json = JSON.stringify(payload);
  if (window.editorBridge?.postMessage) {
    window.editorBridge.postMessage(json);
    return;
  }
  if (window.flutter_inappwebview?.callHandler) {
    window.flutter_inappwebview.callHandler('editorBridge', json);
    return;
  }
  if (window.webkit?.messageHandlers?.editorBridge) {
    window.webkit.messageHandlers.editorBridge.postMessage(json);
    return;
  }
  // React Native WebView.
  if (window.ReactNativeWebView) {
    window.ReactNativeWebView.postMessage(json);
    return;
  }
  if (window.editorBridgeNative?.postMessage) {
    window.editorBridgeNative.postMessage(json);
  }
};

const postToNative = (html) => {
  postNativeMessage({ type: 'content', html });
};
const annotateStagedMedia = (html, stagedInfoByKey) => {
  if (!html || stagedInfoByKey.size === 0 || typeof DOMParser === 'undefined') return html;

  const doc = new DOMParser().parseFromString(html, 'text/html');

  doc.querySelectorAll('img, video').forEach((el) => {
    const staged = stagedInfoByKey.get(el.getAttribute('src'));
    if (!staged) return;
    if (staged.fileName) el.setAttribute('data-file-name', staged.fileName);
    if (staged.fileSizeBytes) el.setAttribute('data-file-size', String(staged.fileSizeBytes));
  });

  return doc.body.innerHTML;
};
function VideoAttachmentBlock(props) {
  const entity = props.contentState.getEntity(props.block.getEntityAt(0));
  const { src, fileName } = entity.getData();
  const { onOpen, viewMode } = props.blockProps;
  const [status, setStatus] = useState('loading');
  const lastTouchAtRef = useRef(0);

  const open = () => onOpen(src, fileName);

  const handlePointerUp = (event) => {
    if (viewMode) {
      open();
      return;
    }

    if (event.pointerType === 'touch' || event.pointerType === 'pen') {
      const now = Date.now();
      if (now - lastTouchAtRef.current <= 360) {
        lastTouchAtRef.current = 0;
        open();
      } else {
        lastTouchAtRef.current = now;
      }
    }
  };

  return (
    <div
      className={`mobile-editor__video-inline${status !== 'ready' ? ' mobile-editor__video-inline--pending' : ''}`}
      contentEditable={false}
      onPointerUp={handlePointerUp}
      onDoubleClick={viewMode ? undefined : open}
    >
      <video
        className="mobile-editor__video-inline-frame"
        src={src}
        preload="metadata"
        muted
        playsInline
        title={fileName || 'Video'}
        onLoadedData={() => setStatus('ready')}
        onLoadedMetadata={() => setStatus((s) => (s === 'loading' ? 'ready' : s))}
        onError={() => setStatus('failed')}
      />
      {status === 'loading' ? (
        <span className="mobile-editor__video-inline-spinner" />
      ) : (
        <span className="mobile-editor__video-inline-play">&#9654;</span>
      )}
    </div>
  );
}

const IMAGE_UPLOAD_ACCEPT = 'image/png,image/jpeg,image/jpg,image/gif,image/webp';

const EDITOR_TEXT_COLORS = [
  'rgb(0,0,0)', 'rgb(65,168,95)', 'rgb(0,168,133)', 'rgb(61,142,185)',
  'rgb(41,105,176)', 'rgb(85,57,130)', 'rgb(40,50,78)', 'rgb(247,218,100)',
  'rgb(251,160,38)', 'rgb(235,107,86)', 'rgb(226,80,65)', 'rgb(163,143,132)',
  'rgb(97,189,109)', 'rgb(26,188,156)', 'rgb(84,172,210)', 'rgb(44,130,201)',
  'rgb(147,101,184)', 'rgb(71,85,119)', 'rgb(204,204,204)', 'rgb(255,255,255)',
];

const ToolbarIcon = ({ children }) => (
  <svg
    className="mobile-editor__toolbar-icon"
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

const IconColor = () => (
  <svg
    className="mobile-editor__toolbar-icon"
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    aria-hidden="true"
  >
    <path d="M6 16 11 5h2l5 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M8.2 12.5h7.6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <rect x="5" y="19" width="14" height="2.6" rx="1.3" fill="#00a0e1" />
  </svg>
);

const IconLink = () => (
  <ToolbarIcon>
    <path d="M9.5 14.5a4 4 0 0 0 5.66 0l2.83-2.83a4 4 0 0 0-5.66-5.66l-1.1 1.1" />
    <path d="M14.5 9.5a4 4 0 0 0-5.66 0L6 12.34a4 4 0 0 0 5.66 5.66l1.1-1.1" />
  </ToolbarIcon>
);

const IconCamera = () => (
  <ToolbarIcon>
    <path d="M4 8h3l1.2-2h6.6L16 8h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z" />
    <circle cx="12" cy="13" r="3.2" />
  </ToolbarIcon>
);

// Insert image by URL.
const IconImage = () => (
  <ToolbarIcon>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <circle cx="8.5" cy="10" r="1.5" />
    <path d="M21 16l-5-5L5 19" />
  </ToolbarIcon>
);

const IconVideo = () => (
  <ToolbarIcon>
    <rect x="3" y="6" width="13" height="12" rx="2" />
    <path d="M16 10l5-3v10l-5-3z" />
  </ToolbarIcon>
);
function ToolbarActionButton({ label, title, onPress }) {
  return (
    <div
      className="rdw-option-wrapper mobile-editor__video-toolbar-button"
      title={title}
      role="button"
      tabIndex={0}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onPress}
    >
      <span className="mobile-editor__video-toolbar-label">{label}</span>
    </div>
  );
}
const KEYBOARD_OCCLUSION_RATIO = 0.10;
const KEYBOARD_MIN_OCCLUSION_PX = 56;
const VIEWPORT_SETTLE_MS = 90;


const BLUR_GRACE_MS = 250;

const TOOLBAR_FALLBACK_HEIGHT = 48;

// The depth ceiling the library's own list indent/outdent control uses.
const MAX_LIST_DEPTH = 4;

const LINE_HEIGHT_PX = 26;

const EDITOR_ACCESS_TOKEN_KEY = 'wwa-editor-access-token';

const getVisibleViewportMetrics = () => {
  const viewport = window.visualViewport;
  const innerHeight = window.innerHeight || document.documentElement.clientHeight || 1;
  const visualHeight = viewport?.height || innerHeight;

  const height = Math.max(1, Math.min(visualHeight, innerHeight));
  const top = Math.max(0, viewport?.offsetTop || 0);

  return { height, top };
};

const getKeyboardThreshold = (baselineHeight) =>
  Math.max(KEYBOARD_MIN_OCCLUSION_PX, baselineHeight * KEYBOARD_OCCLUSION_RATIO);

const supportsPageScrollToolbar = () => {
  const supportsSticky = Boolean(
    window.CSS?.supports?.('position', 'sticky') ||
    window.CSS?.supports?.('position', '-webkit-sticky')
  );

  return Boolean(window.visualViewport) && supportsSticky;
};
const mobileToolbar = {
  ...richTextEditorToolbar,
  options: [
    'history',
    'fontFamily',
    'fontSize',
    'inline',
    'blockType',
    'list',
    'textAlign',
    'remove',
  ],
  link: { ...richTextEditorToolbar.link, visible: true },
};

// ---------------------------------------------------------------------------
// Native toolbar command vocabulary (&toolbar=native).
//
// Every entry below reproduces exactly what the matching in-page react-draft-
// wysiwyg control did, so html written through the native toolbar is
// indistinguishable from html written through the old one. The style keys in
// particular are the library's own: draftjs-utils stores custom inline styles
// as `color-<value>`, `bgcolor-<value>`, `fontsize-<value>`, `fontfamily-<value>`,
// and draftjs-to-html reads those prefixes back out on save.

const INLINE_STYLE_BY_COMMAND = {
  bold: 'BOLD',
  italic: 'ITALIC',
  underline: 'UNDERLINE',
  strikethrough: 'STRIKETHROUGH',
  // The library's inline control calls this one "monospace"; draft calls the
  // style CODE. Accept both spellings so the host can use either.
  code: 'CODE',
  monospace: 'CODE',
};

const BLOCK_TYPES = new Set([
  'unstyled',
  'header-one',
  'header-two',
  'header-three',
  'header-four',
  'header-five',
  'header-six',
  'blockquote',
  'code',
  'unordered-list-item',
  'ordered-list-item',
]);

// The dropdown labels the in-page toolbar showed, so the host can send either
// the label a button is captioned with or draft's own block type.
const BLOCK_TYPE_BY_LABEL = {
  normal: 'unstyled',
  paragraph: 'unstyled',
  h1: 'header-one',
  h2: 'header-two',
  h3: 'header-three',
  h4: 'header-four',
  h5: 'header-five',
  h6: 'header-six',
  blockquote: 'blockquote',
  code: 'code',
  unordered: 'unordered-list-item',
  ordered: 'ordered-list-item',
  bullet: 'unordered-list-item',
  numbered: 'ordered-list-item',
};

// Command names arrive from a native toolbar written by someone reading a table,
// so they must not be casing- or punctuation-sensitive: fontFamily, fontfamily,
// font-family and font all mean the same control. Normalise, then map to the one
// canonical name the dispatcher switches on.
const normalizeCommandName = (name) =>
  String(name).trim().replace(/[\s._-]+/g, '').toLowerCase();

const CANONICAL_COMMAND = {};
const registerCommand = (canonical, ...aliases) => {
  [canonical, ...aliases].forEach((alias) => {
    CANONICAL_COMMAND[normalizeCommandName(alias)] = canonical;
  });
};

registerCommand('bold');
registerCommand('italic');
registerCommand('underline');
registerCommand('strikethrough', 'strike', 'strikeThrough');
registerCommand('code', 'monospace');
registerCommand('inlineStyle');
registerCommand('blockType', 'block', 'heading', 'paragraphStyle');
registerCommand('list');
registerCommand('unorderedList', 'bulletList', 'bullets', 'ul');
registerCommand('orderedList', 'numberedList', 'numbers', 'ol');
registerCommand('indent');
registerCommand('outdent', 'unindent');
registerCommand('textAlign', 'align', 'alignment');
registerCommand('color', 'textColor', 'foreColor', 'fontColor');
registerCommand('bgcolor', 'backColor', 'backgroundColor', 'highlight');
registerCommand('fontSize', 'size', 'textSize');
registerCommand('fontFamily', 'font', 'typeface');
registerCommand('remove', 'removeFormat', 'clearFormatting', 'removeAllInlineStyles');
registerCommand('undo');
registerCommand('redo');
registerCommand('link', 'insertLink');
registerCommand('unlink', 'removeLink');
registerCommand('insertImage', 'imageByUrl', 'insertImageUrl');
registerCommand('insertText');
registerCommand('pickImage', 'image', 'imageFromGallery');
registerCommand('pickVideo', 'video', 'uploadVideo');
registerCommand('insertImageData');
registerCommand('insertVideoData');
registerCommand('openPopup');
registerCommand('closePopup');
registerCommand('focus');

// The pickers the page can open for itself. Under &toolbar=native the library's
// own fontFamily / fontSize / blockType dropdowns are gone with the toolbar that
// owned them, so calling one of these commands with no value has to lead
// somewhere - otherwise the control is unreachable in this model.
const FONT_SIZE_OPTIONS = [8, 9, 10, 11, 12, 14, 16, 18, 24, 30, 36, 48, 60, 72, 96];

const BLOCK_TYPE_OPTIONS = [
  { label: 'Normal', value: 'unstyled' },
  { label: 'H1', value: 'header-one' },
  { label: 'H2', value: 'header-two' },
  { label: 'H3', value: 'header-three' },
  { label: 'H4', value: 'header-four' },
  { label: 'H5', value: 'header-five' },
  { label: 'H6', value: 'header-six' },
  { label: 'Blockquote', value: 'blockquote' },
  { label: 'Code', value: 'code' },
];

const CHOICE_POPUPS = new Set(['fontFamily', 'fontSize', 'blockType']);
const POPUP_NAMES = new Set(['color', 'link', 'imageUrl', ...CHOICE_POPUPS]);

const resolveBlockType = (value) => {
  if (typeof value !== 'string') return null;
  const raw = value.trim();
  if (BLOCK_TYPES.has(raw)) return raw;
  return BLOCK_TYPE_BY_LABEL[raw.toLowerCase()] || null;
};

const TEXT_ALIGNMENTS = new Set(['left', 'center', 'right', 'justify']);

// draftjs-utils prefixes, and the lengths react-draft-wysiwyg's own controls
// slice off when they read the current value back.
const CUSTOM_INLINE_STYLE_PREFIX = {
  COLOR: 'color-',
  BGCOLOR: 'bgcolor-',
  FONTSIZE: 'fontsize-',
  FONTFAMILY: 'fontfamily-',
};

const CUSTOM_INLINE_STYLE_KEYS = Object.keys(CUSTOM_INLINE_STYLE_PREFIX);

const stripCustomStylePrefix = (styles, key) => {
  const value = styles?.[key];
  if (typeof value !== 'string' || !value) return null;
  const prefix = CUSTOM_INLINE_STYLE_PREFIX[key];
  return value.startsWith(prefix) ? value.slice(prefix.length) : value;
};

// What the host needs to light its buttons up. Mirrors the `currentState` each
// react-draft-wysiwyg control derived for itself.
const LIST_NAME_BY_BLOCK_TYPE = {
  'unordered-list-item': 'unordered',
  'ordered-list-item': 'ordered',
};

const buildSelectionSnapshot = (state) => {
  const inline = getSelectionInlineStyle(state) || {};
  const custom = getSelectionCustomInlineStyle(state, CUSTOM_INLINE_STYLE_KEYS) || {};
  const blockType = getSelectedBlocksType(state);
  const metadata = getSelectedBlocksMetadata(state);
  const selection = state.getSelection();

  let linkUrl = null;
  const entityKey = getSelectionEntity(state);
  if (entityKey) {
    const entity = state.getCurrentContent().getEntity(entityKey);
    if (entity?.getType() === 'LINK') linkUrl = entity.getData()?.url || null;
  }

  return {
    bold: Boolean(inline.BOLD),
    italic: Boolean(inline.ITALIC),
    underline: Boolean(inline.UNDERLINE),
    strikethrough: Boolean(inline.STRIKETHROUGH),
    code: Boolean(inline.CODE),
    blockType: blockType || 'unstyled',
    list: LIST_NAME_BY_BLOCK_TYPE[blockType] || null,
    textAlign: metadata?.get?.('text-align') || null,
    color: stripCustomStylePrefix(custom, 'COLOR'),
    bgcolor: stripCustomStylePrefix(custom, 'BGCOLOR'),
    fontSize: stripCustomStylePrefix(custom, 'FONTSIZE'),
    fontFamily: stripCustomStylePrefix(custom, 'FONTFAMILY'),
    link: linkUrl,
    hasSelection: !selection.isCollapsed(),
    canUndo: state.getUndoStack().size > 0,
    canRedo: state.getRedoStack().size > 0,
  };
};

// data: url -> File, so a picture the host picked natively still travels the
// page's own staging/flush pipeline rather than a second, native one.
const fileFromDataUrl = (dataUrl, fileName) => {
  const match = /^data:([^;,]*)(;base64)?,/.exec(dataUrl || '');
  if (!match) return null;

  const mime = match[1] || 'application/octet-stream';
  const payload = dataUrl.slice(match[0].length);

  let bytes;
  if (match[2]) {
    const binary = window.atob(payload);
    bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  } else {
    bytes = new TextEncoder().encode(decodeURIComponent(payload));
  }

  const extension = mime.split('/')[1]?.split('+')[0] || 'bin';
  return new File([bytes], fileName || `upload.${extension}`, { type: mime });
};

export default function EditorWebview() {
  const params = new URLSearchParams(window.location.search);
  const initialHtml = params.get('content') || '';
  const readOnly = params.get('readonly') === 'true';
  const editorSessionToken = params.get('editor_session');
  const taskIdParam = params.get('task') || params.get('taskId');
  const taskId = taskIdParam ? Number(taskIdParam) : null;

  const caretDebug = params.get('caretdebug') === '1';

  const iosDevice = isIOSDevice();
  const caretProxyDebug =
    process.env.NODE_ENV === 'development' && params.get('caretproxy') === '1';

  // &toolbar=native: the host draws the formatting toolbar itself, above the
  // keyboard and outside the web view's frame, and drives it over the bridge.
  //
  // With no toolbar in the page the page no longer needs a fixed-height box, so
  // the document scrolls and there is no composited inner scroller. That is the
  // whole point: past the first tile of an overflow scroller WebKit stops
  // painting the caret and the selection highlight, and therefore never presents
  // the native Select/Copy/Paste callout. Scrolling the document restores all
  // three, which in turn makes the caret proxy and the deep-scroll tap placement
  // below unnecessary.
  //
  // Absent the parameter every derived value keeps the exact expression it had
  // before, on every platform.
  const nativeToolbar = params.get('toolbar') === 'native';
  // &keyboard=host: the app resizes the web view to sit above the keyboard, so the
  // page keeps its own toolbar and pins it with plain CSS. toolbar=native implies it.
  const hostKeyboard = params.get('keyboard') === 'host' || nativeToolbar;

  const useCaretProxy = (iosDevice || caretProxyDebug) && !hostKeyboard;
  const pageScrollRequested = params.get('scroll') === 'page';
  const pageScroll =
    hostKeyboard || (!iosDevice && pageScrollRequested && supportsPageScrollToolbar());

  const [editorState, setEditorState] = useState(() => createEditorStateFromHtml(initialHtml));
  const [sourceHtml, setSourceHtml] = useState(initialHtml);
  const [authState, setAuthState] = useState({
    checking: true,
    allowed: false,
  });
  const [busy, setBusy] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const caretDebugRef = useRef(null);

  const caretProxyRef = useRef(null);
  const [saveError, setSaveError] = useState(null);
  const [selectedBlockKey, setSelectedBlockKey] = useState(null);
  const selectedBlockKeyRef = useRef(null);
  selectedBlockKeyRef.current = selectedBlockKey;
  const [mediaPreview, setMediaPreview] = useState(null);
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const keyboardOpenRef = useRef(false);
  const [activePopup, setActivePopup] = useState(null);
  const [linkUrlInput, setLinkUrlInput] = useState('');
  const [imageUrlInput, setImageUrlInput] = useState('');
  const initialViewport = getVisibleViewportMetrics();
  const [viewportHeight, setViewportHeight] = useState(initialViewport.height);
  const [viewportTop, setViewportTop] = useState(initialViewport.top);
  const closedViewportHeightRef = useRef(initialViewport.height);
  // Set once the viewport has actually shrunk for a keyboard, which makes the
  // viewport - not DOM focus - authoritative for hiding the toolbar again.
  const keyboardConfirmedRef = useRef(false);
  const editorFocusedRef = useRef(false);
  const debounceRef = useRef(null);
  const lastEditAtRef = useRef(0);
  const authCheckStartedRef = useRef(false);
  const toolbarHeightRef = useRef(0);
  const rootRef = useRef(null);
  const areaRef = useRef(null);
  const pointerStartRef = useRef(null);
  const suppressCaretRevealUntilRef = useRef(0);
  const keyBySrcRef = useRef({});
  const pendingFilesRef = useRef(new Map());
  const pasteHydrationRef = useRef(Promise.resolve());
  const lastSelectionStateRef = useRef(null);
  const editorStateRef = useRef(editorState);
  editorStateRef.current = editorState;
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const toPersistableHtml = (state) =>
    persistDescriptionImages(
      getContentHtml(state.getCurrentContent()),
      keyBySrcRef.current
    );

  useEffect(() => {
    if (authCheckStartedRef.current) return;
    authCheckStartedRef.current = true;

    let isMounted = true;

    const validateAccess = async () => {
      if (
        process.env.NODE_ENV === 'development' &&
        new URLSearchParams(window.location.search).get('uitest') === 'true'
      ) {
        if (isMounted) setAuthState({ checking: false, allowed: true });
        return;
      }

      const token = localStorage.getItem('authToken');

      const cachedAccessToken = sessionStorage.getItem(EDITOR_ACCESS_TOKEN_KEY);

      if (cachedAccessToken) {
        setAuthorizationToken(cachedAccessToken);

        if (isMounted) {
          setAuthState({ checking: false, allowed: true });
        }
        return;
      }

      if (!editorSessionToken && !token) {
        if (isMounted) {
          setAuthState({ checking: false, allowed: false });
        }
        return;
      }

      try {
        if (editorSessionToken) {
          const session = await validateEditorSession(editorSessionToken);

          // Short-lived bearer minted by Validate so in-webview API calls
          // (image presign/complete/access) are authenticated.
          const accessToken = session?.AccessToken ?? session?.accessToken;
          if (accessToken) {
            setAuthorizationToken(accessToken);

            try {
              sessionStorage.setItem(EDITOR_ACCESS_TOKEN_KEY, accessToken);
            } catch {
            }
          }

          const cleanParams = new URLSearchParams(window.location.search);
          cleanParams.delete('editor_session');
          const cleanUrl = `${window.location.pathname}?${cleanParams.toString()}`;
          window.history.replaceState(null, '', cleanUrl);
        } else {
          await getLoggedUserFromToken(token);
          setAuthorizationToken(token);
        }

        if (isMounted) {
          setAuthState({ checking: false, allowed: true });
        }
      } catch (error) {
        if (isMounted) {
          setAuthState({ checking: false, allowed: false });
        }
      }
    };

    validateAccess();

    return () => {
      isMounted = false;
    };
  }, [editorSessionToken]);
  useEffect(() => {
    if (!authState.allowed) return undefined;

    const keys = collectDescriptionImageKeys(sourceHtml);
    if (keys.length === 0) return undefined;

    let cancelled = false;

    const resolveImages = async () => {
      setBusy(true);
      const urlByKey = {};
      await Promise.all(
        keys.map(async (key) => {
          const keyTaskId = Number(key.split('/')[1]) || null;
          const url = await resolveTaskImageUrl(key, taskId ?? keyTaskId);
          if (url) urlByKey[key] = url;
        })
      );

      setBusy(false);
      if (cancelled) return;

      const { html, keyBySrc } = applyResolvedImageSrc(sourceHtml, urlByKey);
      keyBySrcRef.current = { ...keyBySrcRef.current, ...keyBySrc };
      setEditorState(createEditorStateFromHtml(html));
    };

    resolveImages();

    return () => {
      cancelled = true;
      setBusy(false);
    };
  }, [authState.allowed, taskId, sourceHtml]);
  const stageImage = async (file, signal) => {
    const compressed = await compressImageForUpload(file).catch(() => file);
    try {
      return { file: compressed, ...(await uploadTaskImageStaged(compressed, signal)) };
    } catch (error) {
      const aborted =
        signal?.aborted ||
        error?.name === 'AbortError' ||
        error?.name === 'CanceledError' ||
        error?.code === 'ERR_CANCELED';
      if (aborted) return { file: compressed };
      const message =
        (typeof error?.getMessage === 'function' && error.getMessage()) ||
        error?.errorData?.Message ||
        error?.response?.data?.Message ||
        error?.message ||
        'Upload failed';
      setUploadError({ fileName: file.name || 'attachment', message });
      return { file: compressed, error: message };
    }
  };

  const addLocalImage = (file) => {
    const blobUrl = URL.createObjectURL(file);
    const controller = new AbortController();
    pendingFilesRef.current.set(blobUrl, {
      promise: stageImage(file, controller.signal),
      controller,
    });
    return blobUrl;
  };
  const abortRemovedImageUploads = (state) => {
    if (pendingFilesRef.current.size === 0) return;

    const currentSrcs = collectImageEntitySrcs(state.getCurrentContent());
    for (const [blobUrl, pending] of Array.from(pendingFilesRef.current.entries())) {
      if (currentSrcs.has(blobUrl)) continue;
      pending.controller.abort();
      URL.revokeObjectURL(blobUrl);
      pendingFilesRef.current.delete(blobUrl);
    }
  };
  const insertImageFile = (file) => {
    const blobUrl = addLocalImage(file);
    editorStateRef.current = handleChange(insertImageBlock(editorStateRef.current, blobUrl));
  };

  const insertVideoFile = (file) => {
    const blobUrl = addLocalImage(file);
    editorStateRef.current = handleChange(insertVideoBlock(editorStateRef.current, blobUrl, file.name));
  };
  const closePopup = () => {
    setActivePopup(null);
    setLinkUrlInput('');
    setImageUrlInput('');
  };

  const applyChoice = (kind, value) => {
    const state = editorStateRef.current;
    if (kind === 'blockType') {
      editorStateRef.current = handleChange(RichUtils.toggleBlockType(state, value));
    } else {
      const next = toggleCustomInlineStyle(state, kind, value);
      if (next) editorStateRef.current = handleChange(next);
    }
    closePopup();
  };

  const applyTextColor = (color) => {
    const next = toggleCustomInlineStyle(editorStateRef.current, 'color', color);
    if (next) editorStateRef.current = handleChange(next);
    closePopup();
  };

  const applyLink = (urlOverride) => {
    const url = (typeof urlOverride === 'string' ? urlOverride : linkUrlInput).trim();
    if (!url) {
      closePopup();
      return;
    }
    const state = editorStateRef.current;
    const selection = state.getSelection();
    const contentWithEntity = state
      .getCurrentContent()
      .createEntity('LINK', 'MUTABLE', { url, targetOption: '_blank' });
    const entityKey = contentWithEntity.getLastCreatedEntityKey();

    let next;
    if (selection.isCollapsed()) {
      const withText = Modifier.insertText(
        contentWithEntity,
        selection,
        url,
        state.getCurrentInlineStyle(),
        entityKey
      );
      next = EditorState.push(state, withText, 'insert-characters');
    } else {
      const withEntity = EditorState.set(state, { currentContent: contentWithEntity });
      next = RichUtils.toggleLink(withEntity, selection, entityKey);
    }
    editorStateRef.current = handleChange(next);
    closePopup();
  };

  const removeLink = () => {
    const state = editorStateRef.current;
    const selection = state.getSelection();
    if (!selection.isCollapsed()) {
      editorStateRef.current = handleChange(RichUtils.toggleLink(state, selection, null));
    }
    closePopup();
  };

  const applyImageUrl = (urlOverride) => {
    const url = (typeof urlOverride === 'string' ? urlOverride : imageUrlInput).trim();
    if (url) editorStateRef.current = handleChange(insertImageBlock(editorStateRef.current, url));
    closePopup();
  };

  const clipboardHasRichText = (clipboard) => {
    const html = clipboard.getData?.('text/html') || '';
    if (!html) return false;
    if (typeof DOMParser === 'undefined') return true;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    return (doc.body?.textContent || '').trim().length > 0;
  };

  const handleMediaPaste = (event) => {
    if (readOnly) return;

    const clipboard = event.clipboardData;
    if (!clipboard) return;

    const fileItems = Array.from(clipboard.items || []).filter(
      (item) =>
        item.kind === 'file' &&
        (item.type.startsWith('image/') || item.type.startsWith('video/'))
    );
    if (fileItems.length === 0) return;
    if (clipboardHasRichText(clipboard)) return;

    const files = fileItems.map((item) => item.getAsFile()).filter(Boolean);
    if (files.length === 0) return;

    event.preventDefault();
    event.stopPropagation();

    files.forEach((file) => {
      if (file.type.startsWith('video/')) {
        insertVideoFile(file);
        return;
      }
      const blobUrl = addLocalImage(file);
      editorStateRef.current = handleChange(insertImageBlock(editorStateRef.current, blobUrl));
    });
  };

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const applyFontFaces = () => {
      root
        .querySelectorAll(
          '.rdw-fontfamily-dropdown .rdw-dropdownoption-default, .rdw-fontfamily-dropdown .rdw-dropdown-selectedtext'
        )
        .forEach((el) => {
          const stack = FONT_FAMILY_STACKS[el.textContent.trim()];

          if (stack) el.style.fontFamily = stack;
        });
    };

    applyFontFaces();

    const observer = new MutationObserver(applyFontFaces);
    observer.observe(root, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [authState.allowed]);

  useEffect(() => {
    let meta = document.querySelector('meta[name="viewport"]');
    let created = false;

    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'viewport';
      meta.content = 'width=device-width, initial-scale=1, viewport-fit=cover';
      document.head.appendChild(meta);
      created = true;
    } else if (!/viewport-fit\s*=\s*cover/i.test(meta.content || '')) {
      meta.content = `${meta.content ? `${meta.content}, ` : ''}viewport-fit=cover`;
    }

    return () => {
      if (created) meta?.remove();
    };
  }, []);

  useEffect(() => {
    const cancel = (event) => event.preventDefault();

    const onTouchMove = (event) => {
      if (event.touches.length > 1) event.preventDefault();
    };

    document.addEventListener('gesturestart', cancel, { passive: false });
    document.addEventListener('gesturechange', cancel, { passive: false });
    document.addEventListener('gestureend', cancel, { passive: false });
    document.addEventListener('touchmove', onTouchMove, { passive: false });

    return () => {
      document.removeEventListener('gesturestart', cancel);
      document.removeEventListener('gesturechange', cancel);
      document.removeEventListener('gestureend', cancel);
      document.removeEventListener('touchmove', onTouchMove);
    };
  }, []);

  useEffect(() => {
    const viewport = window.visualViewport;
    let rafId = 0;
    let settleTimer = 0;

    const apply = () => {
      rafId = 0;

      const { height, top } = getVisibleViewportMetrics();
      const baseline = Math.max(closedViewportHeightRef.current || 0, height);
      const occlusion = Math.max(0, baseline - height);
      const threshold = getKeyboardThreshold(baseline);
      const keyboardByViewport = occlusion > threshold;

      setViewportHeight((previous) => (Math.abs(previous - height) > 0.5 ? height : previous));

      if (!keyboardByViewport && !editorFocusedRef.current) {
        closedViewportHeightRef.current = height;
      }

      if (keyboardByViewport) keyboardConfirmedRef.current = true;

      const focusOpensOptimistically =
        editorFocusedRef.current && !keyboardConfirmedRef.current;

      const nextKeyboardOpen = keyboardByViewport || focusOpensOptimistically;
      keyboardOpenRef.current = nextKeyboardOpen;
      setKeyboardOpen(nextKeyboardOpen);
      setViewportTop(pageScroll || nextKeyboardOpen ? top : 0);
    };

    const schedule = () => {
      if (!rafId) rafId = requestAnimationFrame(apply);
    };

    const resetBaseline = () => {
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(() => {
        const { height } = getVisibleViewportMetrics();
        if (!editorFocusedRef.current) {
          closedViewportHeightRef.current = height;
        }
        schedule();
      }, VIEWPORT_SETTLE_MS);
    };

    apply();
    viewport?.addEventListener('resize', schedule);

    if (!pageScroll) viewport?.addEventListener('scroll', schedule);
    window.addEventListener('resize', schedule);
    window.addEventListener('orientationchange', resetBaseline);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.clearTimeout(settleTimer);
      viewport?.removeEventListener('resize', schedule);
      if (!pageScroll) viewport?.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('orientationchange', resetBaseline);
    };
  }, [pageScroll]);

  useEffect(() => {
    if (!useCaretProxy || readOnly || pageScroll || !authState.allowed) return undefined;

    const root = rootRef.current;
    const scroller = areaRef.current?.querySelector('.rdw-editor-main');
    const content = scroller?.querySelector('.public-DraftEditor-content');
    const proxy = caretProxyRef.current;
    if (!root || !scroller || !content || !proxy) return undefined;

    let rafId = 0;
    let settleTimer = 0;

    const hideProxy = () => {
      proxy.classList.remove('mobile-editor__caret-proxy--visible');
    };

    const updateProxy = () => {
      rafId = 0;

      const selection = window.getSelection();
      const activeElement = document.activeElement;
      if (
        !selection?.isCollapsed ||
        !selection.anchorNode ||
        !scroller.contains(selection.anchorNode) ||
        !activeElement ||
        !content.contains(activeElement)
      ) {
        hideProxy();
        return;
      }

      const closedHeight = closedViewportHeightRef.current || scroller.clientHeight;
      const proxyThreshold = Math.max(120, closedHeight * 0.5);
      if (scroller.scrollTop < proxyThreshold) {
        hideProxy();
        return;
      }

      const range = selection.getRangeAt(0);
      let rect = range.getBoundingClientRect();
      const node = range.startContainer;
      const owner = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
      const ownerRect = owner?.getBoundingClientRect();

      let caretLeft = rect.left;
      let caretTop = rect.top;
      let caretHeight = rect.height;

      if (!rect || (rect.width === 0 && rect.height === 0)) {
        if (!ownerRect) {
          hideProxy();
          return;
        }

        const ownerStyle = window.getComputedStyle(owner);
        const lineHeight = Number.parseFloat(ownerStyle.lineHeight);
        caretHeight = Number.isFinite(lineHeight) ? lineHeight : LINE_HEIGHT_PX;
        caretLeft = ownerRect.left;
        caretTop = ownerRect.top + Math.max(0, (ownerRect.height - caretHeight) / 2);
        rect = ownerRect;
      }

      const scrollerRect = scroller.getBoundingClientRect();
      const caretBottom = caretTop + Math.max(1, caretHeight);
      if (caretBottom < scrollerRect.top || caretTop > scrollerRect.bottom) {
        hideProxy();
        return;
      }

      const rootRect = root.getBoundingClientRect();
      const x = Math.min(Math.max(0, caretLeft - rootRect.left), Math.max(0, rootRect.width - 2));
      const y = caretTop - rootRect.top;

      proxy.style.height = `${Math.max(14, Math.min(48, caretHeight || LINE_HEIGHT_PX))}px`;
      proxy.style.transform = `translate3d(${x}px, ${y}px, 0)`;
      proxy.classList.add('mobile-editor__caret-proxy--visible');
    };

    const scheduleUpdate = () => {
      if (!rafId) rafId = requestAnimationFrame(updateProxy);
    };

    const onScroll = () => {
      hideProxy();
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(scheduleUpdate, 70);
    };

    const onTouchEnd = () => {
      window.clearTimeout(settleTimer);
      scheduleUpdate();
      settleTimer = window.setTimeout(scheduleUpdate, 70);
    };

    const onFocusOut = () => hideProxy();

    scroller.addEventListener('scroll', onScroll, { passive: true });
    scroller.addEventListener('touchstart', hideProxy, { passive: true });
    scroller.addEventListener('touchend', onTouchEnd, { passive: true });
    scroller.addEventListener('focusin', scheduleUpdate);
    scroller.addEventListener('focusout', onFocusOut);
    scroller.addEventListener('input', scheduleUpdate);
    scroller.addEventListener('keyup', scheduleUpdate);
    document.addEventListener('selectionchange', scheduleUpdate);
    window.visualViewport?.addEventListener('resize', onScroll);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.clearTimeout(settleTimer);
      scroller.removeEventListener('scroll', onScroll);
      scroller.removeEventListener('touchstart', hideProxy);
      scroller.removeEventListener('touchend', onTouchEnd);
      scroller.removeEventListener('focusin', scheduleUpdate);
      scroller.removeEventListener('focusout', onFocusOut);
      scroller.removeEventListener('input', scheduleUpdate);
      scroller.removeEventListener('keyup', scheduleUpdate);
      document.removeEventListener('selectionchange', scheduleUpdate);
      window.visualViewport?.removeEventListener('resize', onScroll);
      hideProxy();
    };
  }, [useCaretProxy, readOnly, pageScroll, authState.allowed]);


  useEffect(() => {
    if (readOnly) return undefined;

    const editorRoot = areaRef.current;
    if (!editorRoot) return undefined;

    let blurTimer = 0;

    const onFocusIn = (event) => {
      if (!event.target.closest?.('.public-DraftEditor-content')) return;

      editorFocusedRef.current = true;
      keyboardConfirmedRef.current = false;
      keyboardOpenRef.current = true;
      setKeyboardOpen(true);
    };

    const onFocusOut = (event) => {
      if (!event.target.closest?.('.public-DraftEditor-content')) return;

      editorFocusedRef.current = false;
      window.clearTimeout(blurTimer);
      blurTimer = window.setTimeout(() => {
        const active = document.activeElement;
        const activeKeepsKeyboard = Boolean(
          active?.matches?.('input:not([type="file"]), textarea, [contenteditable="true"]')
        );

        const { height } = getVisibleViewportMetrics();
        const baseline = Math.max(closedViewportHeightRef.current || 0, height);
        const occlusion = Math.max(0, baseline - height);
        const keyboardByViewport = occlusion > getKeyboardThreshold(baseline);
        const nextOpen = activeKeepsKeyboard || keyboardByViewport;

        keyboardOpenRef.current = nextOpen;
        setKeyboardOpen(nextOpen);

        if (!nextOpen) {
          closedViewportHeightRef.current = height;
          setViewportTop(0);
        }
      }, BLUR_GRACE_MS);
    };

    editorRoot.addEventListener('focusin', onFocusIn);
    editorRoot.addEventListener('focusout', onFocusOut);

    return () => {
      window.clearTimeout(blurTimer);
      editorRoot.removeEventListener('focusin', onFocusIn);
      editorRoot.removeEventListener('focusout', onFocusOut);
    };
  }, [readOnly, authState.allowed]);


  const handleChange = (incoming) => {
    const state = ensureTrailingTextBlock(dedupeFontStyles(editorStateRef.current, incoming));


    if (state.getCurrentContent() !== editorStateRef.current.getCurrentContent()) {
      lastEditAtRef.current = Date.now();
    }
    if (
      selectedBlockKeyRef.current &&
      state.getCurrentContent() !== editorStateRef.current.getCurrentContent()
    ) {
      setSelectedBlockKey(null);
    }

    setEditorState(state);
    abortRemovedImageUploads(state);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      postToNative(toPersistableHtml(state));
    }, 300);

    return state;
  };

  const handleKeyCommand = (command, state) => {
    if (
      selectedBlockKeyRef.current &&
      (command === 'backspace' || command === 'delete')
    ) {
      removeSelectedBlock();
      return 'handled';
    }

    const next = removeAtomicBlock(state, command);

    if (next) {
      handleChange(next);
      setSelectedBlockKey(null);

      return 'handled';
    }

    return 'not-handled';
  };

  const keyBindingFn = (event) => {
    const isBackspace = event.key === 'Backspace' || event.keyCode === 8;
    const isDelete = event.key === 'Delete' || event.keyCode === 46;

    if (selectedBlockKeyRef.current) {
      if (isBackspace) return 'backspace';
      if (isDelete) return 'delete';
    }
    const selection = editorStateRef.current.getSelection();
    const block = editorStateRef.current
      .getCurrentContent()
      .getBlockForKey(selection.getStartKey());

    if (block && block.getType() === 'atomic') {
      if (isBackspace) return 'backspace';
      if (isDelete) return 'delete';
    }

    return getDefaultKeyBinding(event);
  };

  const removeSelectedBlock = useCallback(() => {
    const blockKey = selectedBlockKeyRef.current;

    if (!blockKey) return;

    setEditorState((state) => {
      const content = state.getCurrentContent();
      const block = content.getBlockForKey(blockKey);

      if (!block || block.getType() !== 'atomic') return state;
      const selection = SelectionState.createEmpty(blockKey).merge({
        anchorOffset: 0,
        focusOffset: 0,
        hasFocus: true,
      });

      const next = removeAtomicBlock(
        EditorState.forceSelection(state, selection),
        'backspace'
      );

      return next ? ensureTrailingTextBlock(next) : state;
    });

    setSelectedBlockKey(null);
  }, []);

  const basePastedTextHandler = createPastedTextHandler(handleChange);

  const handlePastedText = (text, html, state) => {
    if (html) {
      const handled = basePastedTextHandler(text, html, state);

      const hydration = Promise.resolve()
        .then(() =>
          hydratePastedImages(html, addLocalImage, (from, to) =>
            setEditorState((prev) => replaceImageEntitySrc(prev, from, to))
          )
        )
        .catch(() => { });

      pasteHydrationRef.current = pasteHydrationRef.current.then(() => hydration);
      return handled;
    }

    return basePastedTextHandler(text, html, state);
  };



  const TAP_SLOP_PX = 10;
  const TAP_MAX_MS = 700;
  const TAP_REVEAL_SUPPRESS_MS = 500;



  const placeCaretFromTapPoint = (clientX, clientY, expectedBlockKey) => {
    const contentEl = areaRef.current?.querySelector('.public-DraftEditor-content');
    if (!contentEl) return;


    const viewportTop = window.visualViewport?.offsetTop || 0;
    const candidateYs = [clientY];
    if (viewportTop > 0.5) {
      candidateYs.push(clientY + viewportTop, clientY - viewportTop);
    }

    let range = null;
    let blockElement = null;
    let rangeBlockKey = null;

    for (const candidateY of [...new Set(candidateYs.map((value) => Math.round(value * 2) / 2))]) {
      if (candidateY < 0) continue;

      const candidateRange = getCaretRangeAtPoint(clientX, candidateY);
      if (!candidateRange || !contentEl.contains(candidateRange.startContainer)) continue;

      const rangeNode =
        candidateRange.startContainer.nodeType === Node.ELEMENT_NODE
          ? candidateRange.startContainer
          : candidateRange.startContainer.parentElement;
      const candidateBlock = rangeNode?.closest?.('[data-block="true"]');
      const candidateBlockKey = candidateBlock
        ?.getAttribute('data-offset-key')
        ?.split('-')[0];

      if (!candidateBlock || !candidateBlockKey) continue;
      if (expectedBlockKey && candidateBlockKey !== expectedBlockKey) continue;

      range = candidateRange;
      blockElement = candidateBlock;
      rangeBlockKey = candidateBlockKey;
      break;
    }

    if (!range) {
      const pointElement = document.elementFromPoint(clientX, clientY);
      const pointBlock = pointElement?.closest?.('[data-block="true"]');
      const expectedBlock = expectedBlockKey
        ? [...contentEl.querySelectorAll('[data-block="true"]')].find(
          (element) =>
            element.getAttribute('data-offset-key')?.split('-')[0] === expectedBlockKey
        )
        : null;
      const fallbackBlock = expectedBlock || pointBlock;

      if (fallbackBlock && contentEl.contains(fallbackBlock)) {
        range = getNearestCaretRangeInBlock(fallbackBlock, clientX, clientY);
        blockElement = fallbackBlock;
        rangeBlockKey = fallbackBlock.getAttribute('data-offset-key')?.split('-')[0] || null;
      }
    }

    if (!range || !blockElement || !rangeBlockKey) return;

    const prefixRange = document.createRange();
    prefixRange.selectNodeContents(blockElement);
    try {
      prefixRange.setEnd(range.startContainer, range.startOffset);
    } catch {
      return;
    }

    const requestedOffset = prefixRange.toString().length;
    const state = editorStateRef.current;
    const block = state.getCurrentContent().getBlockForKey(rangeBlockKey);
    if (!block || block.getType() === 'atomic') return;

    const offset = Math.max(0, Math.min(requestedOffset, block.getLength()));
    const nextSelection = SelectionState.createEmpty(rangeBlockKey).merge({
      anchorKey: rangeBlockKey,
      anchorOffset: offset,
      focusKey: rangeBlockKey,
      focusOffset: offset,
      isBackward: false,
      hasFocus: true,
    });

    const nativeSelection = window.getSelection();
    nativeSelection?.removeAllRanges();
    nativeSelection?.addRange(range);

    suppressCaretRevealUntilRef.current = Date.now() + TAP_REVEAL_SUPPRESS_MS;
    setEditorState((current) => EditorState.forceSelection(current, nextSelection));
  };

  const handleAreaPointerDown = (event) => {
    if (readOnly || event.isPrimary === false) return;
    suppressCaretRevealUntilRef.current = Date.now() + TAP_MAX_MS;
    pointerStartRef.current = {
      x: event.clientX,
      y: event.clientY,
      time: Date.now(),
    };
  };

  const wasTap = (event) => {
    const start = pointerStartRef.current;
    if (!start) return false;
    const movedX = Math.abs(event.clientX - start.x);
    const movedY = Math.abs(event.clientY - start.y);
    return (
      movedX <= TAP_SLOP_PX &&
      movedY <= TAP_SLOP_PX &&
      Date.now() - start.time <= TAP_MAX_MS
    );
  };

  const focusEditorForTap = (target) => {
    const contentEl = areaRef.current?.querySelector('.public-DraftEditor-content');
    if (!contentEl) return;

    const focusContent = () => {
      if (document.activeElement === contentEl) return;
      try {
        contentEl.focus({ preventScroll: true });
      } catch {
        contentEl.focus();
      }
    };

    if (target.closest('.public-DraftEditor-content')) {

      if (!iosDevice) focusContent();
      return;
    }

    focusContent();
    setEditorState((state) => {
      const last = state.getCurrentContent().getLastBlock();
      if (last.getType() === 'atomic') {
        return placeCaretAfterBlock(state, last.getKey()) || state;
      }
      return EditorState.moveFocusToEnd(state);
    });
  };

  const handleContainerPointerUp = useCallback((event) => {
    if (event.isPrimary === false) return;

    const target = event.target instanceof Element ? event.target : event.target?.parentElement;
    if (!target || target.closest('.rdw-editor-toolbar')) return;

    // A scroll, not a tap: leave focus, the keyboard and the selection alone.
    const tap = wasTap(event);
    pointerStartRef.current = null;
    if (!tap) {
      suppressCaretRevealUntilRef.current = 0;
      return;
    }


    suppressCaretRevealUntilRef.current = Date.now() + TAP_REVEAL_SUPPRESS_MS;

    if (hostKeyboard && !readOnly) {
      const href = target.closest('a[href]')?.getAttribute('href');
      if (href && target.closest('.public-DraftEditor-content')) {
        postNativeMessage({ type: 'openLink', url: href });
      }
    }

    if (!readOnly) focusEditorForTap(target);

    if (readOnly) {
      if (target.tagName === 'IMG' && target.closest('.public-DraftEditor-content')) {
        setMediaPreview({ kind: 'image', src: target.src });
      }
      return;
    }

    if (!target.closest('.public-DraftEditor-content')) {
      setSelectedBlockKey(null);
      return;
    }

    const offsetKey = target
      .closest('[data-block="true"]')
      ?.getAttribute('data-offset-key');
    const blockKey = offsetKey?.split('-')[0];
    const content = editorStateRef.current.getCurrentContent();
    const clickedBlock = blockKey ? content.getBlockForKey(blockKey) : null;

    if (clickedBlock?.getType() === 'atomic') {
      setSelectedBlockKey(blockKey);
      setEditorState((state) => placeCaretAfterBlock(state, blockKey) || state);
      return;
    }

    if (useCaretProxy && keyboardOpenRef.current) {
      const scroller = areaRef.current?.querySelector('.rdw-editor-main');
      const closedHeight = closedViewportHeightRef.current || scroller?.clientHeight || 1;
      const deepSelectionRegion = (scroller?.scrollTop || 0) >= Math.max(120, closedHeight * 0.5);

      if (deepSelectionRegion) {
        const { clientX, clientY } = event;

        requestAnimationFrame(() => {
          placeCaretFromTapPoint(clientX, clientY, blockKey);
        });
      }
    }

    if (selectedBlockKeyRef.current) {
      requestAnimationFrame(() => setSelectedBlockKey(null));
    }

    if (!clickedBlock) {
      setEditorState((state) => {
        const last = state.getCurrentContent().getLastBlock();
        if (last.getType() !== 'atomic') return state;
        return placeCaretAfterBlock(state, last.getKey()) || state;
      });
    }
  }, [readOnly, useCaretProxy, hostKeyboard]);

  const handleAreaTouchStart = (event) => {
    if (window.PointerEvent || readOnly) return;
    const touch = event.touches?.[0];
    if (!touch) return;

    suppressCaretRevealUntilRef.current = Date.now() + TAP_MAX_MS;
    pointerStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now(),
    };
  };

  const handleContainerTouchEnd = (event) => {
    if (window.PointerEvent) return;
    const touch = event.changedTouches?.[0];
    if (!touch) return;

    handleContainerPointerUp({
      isPrimary: true,
      target: event.target,
      clientX: touch.clientX,
      clientY: touch.clientY,
    });
  };
  useEffect(() => {
    const container = areaRef.current;
    if (!container) return undefined;

    const applySelection = () => {
      container
        .querySelectorAll('.wwa-image-selected')
        .forEach((el) => el.classList.remove('wwa-image-selected'));
      if (!selectedBlockKey) return;
      container
        .querySelector(
          `[data-offset-key^="${selectedBlockKey}-"] img, [data-offset-key^="${selectedBlockKey}-"] .mobile-editor__video-inline`
        )
        ?.classList.add('wwa-image-selected');
    };

    applySelection();


    let scheduled = 0;
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = requestAnimationFrame(() => {
        scheduled = 0;
        applySelection();
      });
    });
    observer.observe(container, { childList: true, subtree: true });
    return () => {
      if (scheduled) cancelAnimationFrame(scheduled);
      observer.disconnect();
    };
  }, [selectedBlockKey, editorState]);


  useEffect(() => {
    const html = document.documentElement;
    const { body } = document;
    const previous = {
      htmlHeight: html.style.height,
      htmlOverflow: html.style.overflow,
      bodyHeight: body.style.height,
      bodyOverflow: body.style.overflow,
      htmlBackground: html.style.backgroundColor,
      bodyBackground: body.style.backgroundColor,
    };

    html.style.backgroundColor = '#ffffff';
    body.style.backgroundColor = '#ffffff';

    if (pageScroll) {
      html.style.height = 'auto';
      html.style.overflow = 'visible';
      body.style.height = 'auto';
      body.style.overflow = 'visible';
    } else {
      html.style.height = '100%';
      html.style.overflow = 'hidden';
      body.style.height = '100%';
      body.style.overflow = 'hidden';

      window.scrollTo(0, 0);
    }

    return () => {
      html.style.height = previous.htmlHeight;
      html.style.overflow = previous.htmlOverflow;
      body.style.height = previous.bodyHeight;
      body.style.overflow = previous.bodyOverflow;
      html.style.backgroundColor = previous.htmlBackground;
      body.style.backgroundColor = previous.bodyBackground;
    };
  }, [pageScroll]);

  useEffect(() => {
    if (!caretDebug) return undefined;

    const update = () => {
      try {
        const state = editorStateRef.current;
        const sel = state.getSelection();
        const domSel = window.getSelection();
        let rect = null;
        if (domSel && domSel.rangeCount > 0) {
          rect = domSel.getRangeAt(0).getBoundingClientRect();
        }
        const scroller = areaRef.current?.querySelector('.rdw-editor-main');
        const sRect = scroller?.getBoundingClientRect();
        const visible =
          rect && sRect
            ? rect.top >= sRect.top - 2 && rect.bottom <= sRect.bottom + 2
            : null;

        if (caretDebugRef.current) {
          caretDebugRef.current.textContent = [
            `sel ${sel.getAnchorKey()}@${sel.getAnchorOffset()} focus=${sel.getHasFocus()}`,
            rect
              ? `rect t=${Math.round(rect.top)} l=${Math.round(rect.left)} h=${Math.round(rect.height)}`
              : 'rect none',
            scroller
              ? `scr ${Math.round(scroller.scrollTop)}/${scroller.clientHeight}/${scroller.scrollHeight}`
              : 'scr none',
            `vis ${visible === null ? '?' : visible ? 'YES' : 'NO'}`,
          ].join('  ');
        }
      } catch (error) {
        if (caretDebugRef.current) caretDebugRef.current.textContent = `err ${error.message}`;
      }
    };

    update();
    const id = setInterval(update, 500);
    document.addEventListener('selectionchange', update);
    return () => {
      clearInterval(id);
      document.removeEventListener('selectionchange', update);
    };
  }, [caretDebug]);


  useEffect(() => {
    if (readOnly) return undefined;
    const container = areaRef.current;
    if (!container) return undefined;

    const relocateNonEditable = () => {

      container.querySelectorAll('figure[data-block="true"]').forEach((figure) => {
        const inner = figure.firstElementChild;
        if (!inner) return;
        if (figure.hasAttribute('contenteditable')) {
          figure.removeAttribute('contenteditable');
        }
        if (inner.getAttribute('contenteditable') !== 'false') {
          inner.setAttribute('contenteditable', 'false');
        }
      });
    };

    const options = {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['contenteditable'],
    };


    let scheduled = 0;
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = requestAnimationFrame(() => {
        scheduled = 0;
        observer.disconnect();
        relocateNonEditable();
        observer.observe(container, options);
      });
    });

    relocateNonEditable();
    observer.observe(container, options);
    return () => {
      if (scheduled) cancelAnimationFrame(scheduled);
      observer.disconnect();
    };
  }, [readOnly, editorState]);
  useEffect(() => {
    if (readOnly || !iosDevice) return undefined;
    const root = rootRef.current;
    if (!root) return undefined;

    const POPUP_SELECTOR =
      '.rdw-dropdown-optionwrapper, .rdw-colorpicker-modal, .rdw-link-modal, .rdw-image-modal';

    const applyPopupState = () => {
      root.classList.toggle(
        'mobile-editor--popup-open',
        Boolean(root.querySelector(POPUP_SELECTOR))
      );
    };

    applyPopupState();

    const observer = new MutationObserver(applyPopupState);
    observer.observe(root, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      root.classList.remove('mobile-editor--popup-open');
    };
  }, [readOnly, authState.allowed]);

  useEffect(() => {
    if (!selectedBlockKey) return undefined;
    const handleOutsideClick = (e) => {
      if (!areaRef.current?.contains(e.target)) setSelectedBlockKey(null);
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [selectedBlockKey]);
  useEffect(() => attachImageFallback(areaRef.current), []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const apply = (height) => {
      root.style.setProperty('--toolbar-h', `${Math.round(height)}px`);
      toolbarHeightRef.current = height;
      const scroller = root.querySelector('.rdw-editor-main');

      if (scroller) {
        const existing = scroller.querySelector('.mobile-editor__scroll-spacer');

        if (pageScroll) {
          if (existing) existing.remove();
          return;
        }

        let spacer = existing;

        if (!spacer) {
          spacer = window.document.createElement('div');
          spacer.className = 'mobile-editor__scroll-spacer';
          spacer.setAttribute('contenteditable', 'false');
          spacer.setAttribute('aria-hidden', 'true');
        }

        const scrollSlack = Math.max(24, Math.round(height) + 2 * LINE_HEIGHT_PX);
        spacer.style.height = `${scrollSlack}px`;
        scroller.appendChild(spacer);
      }
    };

    const toolbar = root.querySelector('.mobile-editor__toolbar');

    if (!toolbar) {
      apply(readOnly || nativeToolbar ? 0 : TOOLBAR_FALLBACK_HEIGHT);
      return undefined;
    }

    apply(toolbar.getBoundingClientRect().height);

    if (typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(() => {
      apply(toolbar.getBoundingClientRect().height);
    });

    observer.observe(toolbar);

    return () => observer.disconnect();
  }, [keyboardOpen, readOnly, authState.allowed, pageScroll, nativeToolbar]);

  useEffect(() => {
    if (readOnly) return undefined;

    let rafId = 0;
    let pendingReason = 'selection';

    const revealCaret = () => {
      rafId = 0;
      const reason = pendingReason;
      pendingReason = 'selection';

      const recentlyEdited = Date.now() - lastEditAtRef.current < 1000;
      if (reason === 'selection') {
        if (Date.now() < suppressCaretRevealUntilRef.current) return;
        if (!recentlyEdited) return;
      }

      const scroller = areaRef.current?.querySelector('.rdw-editor-main');
      const selection = window.getSelection();
      if (!scroller || !selection || selection.rangeCount === 0 || !selection.isCollapsed) return;

      const range = selection.getRangeAt(0);
      if (!scroller.contains(range.startContainer)) return;

      let rect = range.getBoundingClientRect();
      if (!rect || (rect.top === 0 && rect.bottom === 0)) {
        // Collapsed ranges in empty blocks report a zero rect - fall back to
        // the containing element.
        const node = range.startContainer;
        const element = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
        if (!element) return;
        rect = element.getBoundingClientRect();
      }
      const margin = 12;
      const toolbarInset = margin + (toolbarHeightRef.current || 0);


      const lookahead = reason === 'selection' ? LINE_HEIGHT_PX : 0;


      const vv = window.visualViewport;
      const scrollerRect = scroller.getBoundingClientRect();
      const viewTop = pageScroll ? (vv ? vv.offsetTop : 0) : scrollerRect.top;
      const viewBottom = pageScroll
        ? viewTop + (vv ? vv.height : window.innerHeight)
        : scrollerRect.bottom;

      const usableTop = viewTop + margin;
      const usableBottom = viewBottom - toolbarInset;
      if (usableBottom <= usableTop) return;


      if (rect.top >= usableTop && rect.bottom <= usableBottom) return;


      if (rect.bottom <= usableBottom) return;
      const delta = rect.bottom - usableBottom + lookahead;

      if (Math.abs(delta) < 1) return;

      if (pageScroll) {
        window.scrollBy(0, delta);
      } else {
        scroller.scrollTop += delta;
      }
    };

    const schedule = (reason) => {
      if (reason === 'viewport') pendingReason = 'viewport';
      if (!rafId) rafId = requestAnimationFrame(revealCaret);
    };

    const onSelectionChange = () => schedule('selection');
    const onViewportResize = () => schedule('viewport');

    document.addEventListener('selectionchange', onSelectionChange);
    window.visualViewport?.addEventListener('resize', onViewportResize);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      document.removeEventListener('selectionchange', onSelectionChange);
      window.visualViewport?.removeEventListener('resize', onViewportResize);
    };
  }, [readOnly, pageScroll]);

  const postBridgeMessage = (payload) => {
    postNativeMessage(payload);
  };

  // --- Native toolbar command dispatcher -----------------------------------
  //
  // Every command reads editorStateRef.current rather than a captured
  // editorState: these arrive from evaluateJavaScript while the web view does
  // not hold focus, and draft-js keeps the real selection in its own model, not
  // in the DOM. Everything routes through handleChange so the debounced
  // `content` post, the font-style dedupe and the trailing-block guarantee all
  // still happen, exactly as they did for the in-page toolbar.
  const runCommand = (rawName, value) => {
    if (typeof rawName !== 'string' || !rawName.trim()) return false;
    const name = CANONICAL_COMMAND[normalizeCommandName(rawName)];
    if (!name) return false;

    const hasValue = value !== null && value !== undefined && value !== '';
    const state = editorStateRef.current;
    const apply = (next) => {
      if (next) editorStateRef.current = handleChange(next);
    };

    const inlineStyle =
      INLINE_STYLE_BY_COMMAND[name] ||
      (name === 'inlineStyle' && typeof value === 'string'
        ? INLINE_STYLE_BY_COMMAND[value.toLowerCase()] || value.toUpperCase()
        : null);

    if (inlineStyle) {
      apply(RichUtils.toggleInlineStyle(state, inlineStyle));
      return true;
    }

    switch (name) {
      case 'blockType':
      case 'list': {
        if (!hasValue && name === 'blockType' && !readOnly) {
          setActivePopup('blockType');
          return true;
        }
        const blockType = resolveBlockType(value);
        if (!blockType) return false;
        apply(RichUtils.toggleBlockType(state, blockType));
        return true;
      }
      case 'indent':
        apply(changeDepth(state, 1, MAX_LIST_DEPTH));
        return true;

      case 'outdent':
        apply(changeDepth(state, -1, MAX_LIST_DEPTH));
        return true;

      case 'unorderedList':
        apply(RichUtils.toggleBlockType(state, 'unordered-list-item'));
        return true;

      case 'orderedList':
        apply(RichUtils.toggleBlockType(state, 'ordered-list-item'));
        return true;

      case 'textAlign': {
        const alignment = typeof value === 'string' ? value.trim().toLowerCase() : '';
        const next = TEXT_ALIGNMENTS.has(alignment) ? alignment : undefined;
        apply(setBlockData(state, { 'text-align': next }));
        return true;
      }

      case 'color':
      case 'bgcolor':
      case 'fontSize':
      case 'fontFamily': {
        if (!hasValue) {
          if (name === 'bgcolor' || readOnly) return false;
          setActivePopup(name === 'color' ? 'color' : name);
          return true;
        }
        const next = toggleCustomInlineStyle(state, name, value);
        if (next) apply(next);
        return true;
      }

      case 'remove':
      case 'removeFormat':
        apply(removeAllInlineStyles(state));
        return true;

      case 'undo':
        apply(EditorState.undo(state));
        return true;

      case 'redo':
        apply(EditorState.redo(state));
        return true;

      case 'link': {
        const url = typeof value === 'string' ? value : value?.url;
        if (typeof url !== 'string' || !url.trim()) {
          if (readOnly) return false;
          setActivePopup('link');
          return true;
        }
        applyLink(url);
        return true;
      }

      case 'unlink': {
        const selection = state.getSelection();
        if (!selection.isCollapsed()) {
          apply(RichUtils.toggleLink(state, selection, null));
          return true;
        }
        const entityKey = getSelectionEntity(state);
        if (!entityKey) return true;
        const range = getEntityRange(state, entityKey);
        if (!range) return true;

        apply(
          RichUtils.toggleLink(
            state,
            selection.merge({
              anchorOffset: range.start,
              focusOffset: range.end,
              isBackward: false,
            }),
            null
          )
        );
        return true;
      }

      case 'insertImage': {
        const url = typeof value === 'string' ? value : value?.url;
        if (typeof url !== 'string' || !url.trim()) {
          if (readOnly) return false;
          setActivePopup('imageUrl');
          return true;
        }
        applyImageUrl(url);
        return true;
      }

      case 'insertText': {
        const text = typeof value === 'string' ? value : value?.text;
        if (typeof text !== 'string' || !text) return false;
        const content = Modifier.replaceText(
          state.getCurrentContent(),
          state.getSelection(),
          text,
          state.getCurrentInlineStyle()
        );
        apply(EditorState.push(state, content, 'insert-characters'));
        return true;
      }
      case 'pickImage':
        imageInputRef.current?.click();
        return true;

      case 'pickVideo':
        videoInputRef.current?.click();
        return true;
      case 'insertImageData':
      case 'insertVideoData': {
        const dataUrl = typeof value === 'string' ? value : value?.dataUrl;
        const file = fileFromDataUrl(dataUrl, value?.fileName);
        if (!file) return false;
        if (name === 'insertImageData') insertImageFile(file);
        else insertVideoFile(file);
        return true;
      }
      case 'openPopup': {
        if (readOnly) return false;
        if (!POPUP_NAMES.has(value)) return false;
        setActivePopup(value);
        return true;
      }

      case 'closePopup':
        closePopup();
        return true;

      case 'focus': {
        const contentEl = areaRef.current?.querySelector('.public-DraftEditor-content');
        if (!contentEl) return false;
        try {
          contentEl.focus({ preventScroll: true });
        } catch {
          contentEl.focus();
        }
        return true;
      }

      default:
        return false;
    }
  };

  const runCommandRef = useRef(runCommand);
  runCommandRef.current = runCommand;

  useEffect(() => {
    const flush = async (requestId, taskIdOverride) => {
      await pasteHydrationRef.current;

      const id = taskIdOverride ?? taskId;
      const rawHtml = getContentHtml(editorStateRef.current.getCurrentContent());

      try {
        let stagedInfoByKey = null;

        if (pendingFilesRef.current.size > 0) {
          setBusy(true);

          const entries = Array.from(pendingFilesRef.current.entries());
          const toFlush = entries.filter(([blobUrl]) => rawHtml.includes(blobUrl));

          for (const [blobUrl] of entries.filter(([blobUrl]) => !rawHtml.includes(blobUrl))) {
            URL.revokeObjectURL(blobUrl);
            pendingFilesRef.current.delete(blobUrl);
          }

          if (id) {
            await Promise.all(
              toFlush.map(async ([blobUrl, pending]) => {
                let staged = await pending.promise;
                if (!staged.stagingKey) {
                  staged = { file: staged.file, ...(await uploadTaskImageStaged(staged.file)) };
                }
                const { key } = await commitTaskImage(staged, id);
                keyBySrcRef.current[blobUrl] = `/${key}`;
                URL.revokeObjectURL(blobUrl);
                pendingFilesRef.current.delete(blobUrl);
              })
            );
          } else {
            stagedInfoByKey = new Map();
            await Promise.all(
              toFlush.map(async ([blobUrl, pending]) => {
                let staged = await pending.promise;
                if (!staged.stagingKey) {
                  staged = { file: staged.file, ...(await uploadTaskImageStaged(staged.file)) };
                }
                keyBySrcRef.current[blobUrl] = `/${staged.stagingKey}`;
                stagedInfoByKey.set(`/${staged.stagingKey}`, staged);
                pendingFilesRef.current.delete(blobUrl);
              })
            );
          }
        }

        let html = persistDescriptionImages(rawHtml, keyBySrcRef.current);
        if (stagedInfoByKey && stagedInfoByKey.size > 0) {
          html = annotateStagedMedia(html, stagedInfoByKey);
        }
        postBridgeMessage({ type: 'flushed', requestId, html });
        return html;
      } catch (error) {
        const serverMessage =
          error?.response?.data?.Message ||
          (typeof error?.response?.data === 'string' ? error.response.data : null);

        const message = serverMessage || error?.message || 'Image upload failed';

        setSaveError(message);

        postBridgeMessage({ type: 'flushError', requestId, message });
        throw error;
      } finally {
        setBusy(false);
      }
    };

    const discardUploads = () => {
      pendingFilesRef.current.forEach((pending, blobUrl) => {
        pending.controller.abort();
        URL.revokeObjectURL(blobUrl);
      });
      pendingFilesRef.current.clear();
    };

    window.EditorBridge = {
      getContent: () => toPersistableHtml(editorState),
      setContent: (html) => {
        setEditorState(createEditorStateFromHtml(html));
        setSourceHtml(html || '');
      },
      flush,
      discardUploads,
      command: (name, value) => {
        try {
          return runCommandRef.current?.(name, value) === true;
        } catch {
          return false;
        }
      },
      getSelectionState: () => buildSelectionSnapshot(editorStateRef.current),
      getToolbarOptions: () => ({
        fontFamily: mobileToolbar.fontFamily.options,
        fontSize: FONT_SIZE_OPTIONS,
        blockType: BLOCK_TYPE_OPTIONS,
        color: EDITOR_TEXT_COLORS,
        textAlign: ['left', 'center', 'right', 'justify'],
        commands: Array.from(new Set(Object.values(CANONICAL_COMMAND))).sort(),
      }),
    };
  }, [editorState, taskId]);
  useEffect(() => {
    if (!nativeToolbar || readOnly || !authState.allowed) return;

    let snapshot;
    try {
      snapshot = buildSelectionSnapshot(editorState);
    } catch {
      return;
    }

    const serialised = JSON.stringify(snapshot);
    if (serialised === lastSelectionStateRef.current) return;
    lastSelectionStateRef.current = serialised;

    postNativeMessage({ type: 'selectionState', ...snapshot });
  }, [editorState, nativeToolbar, readOnly, authState.allowed]);

  useEffect(() => {
    const revokeAll = () => {
      pendingFilesRef.current.forEach((pending, blobUrl) => {
        pending.controller.abort();
        URL.revokeObjectURL(blobUrl);
      });
      pendingFilesRef.current.clear();
    };
    window.addEventListener('pagehide', revokeAll);
    return () => {
      window.removeEventListener('pagehide', revokeAll);
      revokeAll();
    };
  }, []);

  if (authState.checking) {
    return <div style={styles.message}>Loading editor...</div>;
  }

  if (!authState.allowed) {
    return <div style={styles.message}>Unauthorized</div>;
  }

  const showToolbar = !readOnly && keyboardOpen && !nativeToolbar;

  let selectionSnapshot = {};
  if (CHOICE_POPUPS.has(activePopup)) {
    try {
      selectionSnapshot = buildSelectionSnapshot(editorState);
    } catch {
      selectionSnapshot = {};
    }
  }

  const videoBlockRendererFn = (contentBlock, config) => {
    if (contentBlock.getType() !== 'atomic') return undefined;
    const entityKey = contentBlock.getEntityAt(0);
    if (!entityKey) return undefined;
    if (config.getEditorState().getCurrentContent().getEntity(entityKey).getType() !== 'VIDEO') {
      return undefined;
    }
    return {
      component: VideoAttachmentBlock,
      editable: false,
      props: { onOpen: (src, fileName) => setMediaPreview({ kind: 'video', src, fileName }), viewMode: readOnly },
    };
  };
  const toolbar = mobileToolbar;

  return (
    <div
      ref={rootRef}
      className={`mobile-editor${iosDevice ? ' mobile-editor--ios' : ''}${keyboardOpen ? ' mobile-editor--keyboard-open' : ''
        }${caretDebug ? ' mobile-editor--caretdebug' : ''}${pageScroll ? ' mobile-editor--pagescroll' : ''}${hostKeyboard ? ' mobile-editor--host-keyboard' : ''}${nativeToolbar ? ' mobile-editor--native-toolbar' : ''}`}
      style={{
        ...styles.page,

        ...(pageScroll ? {} : { top: `${viewportTop}px`, height: `${viewportHeight}px` }),
        '--vvh': `${viewportHeight}px`,
        '--vvtop': `${viewportTop}px`,
      }}
    >
      <style>{mobileEditorCss}</style>
      {useCaretProxy && !readOnly && (
        <div
          ref={caretProxyRef}
          className="mobile-editor__caret-proxy"
          aria-hidden="true"
        />
      )}
      {caretDebug && (
        <div className="mobile-editor__caretdebug" ref={caretDebugRef}>
          measuring...
        </div>
      )}
      {busy && <div className="mobile-editor__busy"><span className="mobile-editor__spinner" /></div>}
      {uploadError && (
        <div className="mobile-editor__error" role="alert">
          <span className="mobile-editor__error-text">
            {`Couldn't upload "${uploadError.fileName}": ${uploadError.message}. `}
            Remove it before saving, or try again.
          </span>
          <button
            type="button"
            className="mobile-editor__error-dismiss"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setUploadError(null)}
            aria-label="Dismiss"
          >
            &times;
          </button>
        </div>
      )}
      {saveError && (
        <div className="mobile-editor__save-error" role="alert">
          <span>{saveError}</span>
          <button type="button" onClick={() => setSaveError(null)}>Dismiss</button>
        </div>
      )}

      <div
        className="mobile-editor__area"
        ref={areaRef}
        onPointerDown={handleAreaPointerDown}
        onPointerUpCapture={handleContainerPointerUp}
        onTouchStart={handleAreaTouchStart}
        onTouchEndCapture={handleContainerTouchEnd}
        onPasteCapture={handleMediaPaste}
      >
        <Editor
          editorState={editorState}
          onEditorStateChange={handleChange}
          handlePastedText={handlePastedText}
          readOnly={readOnly}
          toolbarHidden={!showToolbar}
          handleKeyCommand={handleKeyCommand}
          keyBindingFn={keyBindingFn}
          spellCheck
          customStyleMap={fontFamilyStyleMap}
          wrapperClassName="mobile-editor__wrapper"
          editorClassName="mobile-editor__editor"
          toolbarClassName="mobile-editor__toolbar"
          toolbar={toolbar}
          customBlockRenderFunc={videoBlockRendererFn}
          toolbarCustomButtons={
            readOnly
              ? []
              : [
                <ToolbarActionButton
                  key="color"
                  title="Text colour"
                  label={<IconColor />}
                  onPress={() => setActivePopup('color')}
                />,
                <ToolbarActionButton
                  key="link"
                  title="Insert link"
                  label={<IconLink />}
                  onPress={() => setActivePopup('link')}
                />,
                <ToolbarActionButton
                  key="image-upload"
                  title="Image from gallery"
                  label={<IconCamera />}
                  onPress={() => imageInputRef.current?.click()}
                />,
                <ToolbarActionButton
                  key="image-url"
                  title="Insert image by URL"
                  label={<IconImage />}
                  onPress={() => setActivePopup('imageUrl')}
                />,
                <ToolbarActionButton
                  key="video-upload"
                  title="Upload video"
                  label={<IconVideo />}
                  onPress={() => videoInputRef.current?.click()}
                />,
              ]
          }
        />
      </div>
      <input
        ref={imageInputRef}
        type="file"
        accept={IMAGE_UPLOAD_ACCEPT}
        className="mobile-editor__hidden-file-input"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) insertImageFile(file);
        }}
      />
      <input
        ref={videoInputRef}
        type="file"
        accept="video/*"
        className="mobile-editor__hidden-file-input"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) insertVideoFile(file);
        }}
      />
      {mediaPreview && (
        <div
          className="mobile-editor__video-overlay"
          onClick={() => setMediaPreview(null)}
        >
          {mediaPreview.kind === 'image' ? (
            <img
              className="mobile-editor__video-player"
              src={mediaPreview.src}
              alt={mediaPreview.fileName || ''}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <video
              key={mediaPreview.src}
              className="mobile-editor__video-player"
              src={mediaPreview.src}
              controls
              autoPlay
              onClick={(e) => e.stopPropagation()}
            />
          )}
        </div>
      )}
      {activePopup && (
        <div
          className="mobile-editor__popup-backdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget) closePopup();
          }}
        >
          <div className="mobile-editor__popup">
            {CHOICE_POPUPS.has(activePopup) && (() => {
              const config = {
                fontFamily: {
                  title: 'Font',
                  options: mobileToolbar.fontFamily.options.map((font) => ({
                    label: font,
                    value: font,
                    style: { fontFamily: FONT_FAMILY_STACKS[font] || font },
                  })),
                  current: selectionSnapshot.fontFamily,
                },
                fontSize: {
                  title: 'Font size',
                  options: FONT_SIZE_OPTIONS.map((size) => ({
                    label: String(size),
                    value: size,
                  })),
                  current: selectionSnapshot.fontSize,
                },
                blockType: {
                  title: 'Paragraph style',
                  options: BLOCK_TYPE_OPTIONS,
                  current: selectionSnapshot.blockType,
                },
              }[activePopup];

              return (
                <>
                  <div className="mobile-editor__popup-title">{config.title}</div>
                  <div className="mobile-editor__choice-list">
                    {config.options.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className={`mobile-editor__choice${String(option.value) === String(config.current)
                          ? ' mobile-editor__choice--active'
                          : ''
                          }`}
                        style={option.style}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => applyChoice(activePopup, option.value)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                  <div className="mobile-editor__popup-actions">
                    <button type="button" className="mobile-editor__popup-btn" onClick={closePopup}>
                      Close
                    </button>
                  </div>
                </>
              );
            })()}

            {activePopup === 'color' && (
              <>
                <div className="mobile-editor__popup-title">Text colour</div>
                <div className="mobile-editor__color-grid">
                  {EDITOR_TEXT_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      className="mobile-editor__color-swatch"
                      style={{ backgroundColor: color }}
                      title={color}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => applyTextColor(color)}
                    />
                  ))}
                </div>
                <div className="mobile-editor__popup-actions">
                  <button type="button" className="mobile-editor__popup-btn" onClick={closePopup}>
                    Close
                  </button>
                </div>
              </>
            )}

            {activePopup === 'link' && (
              <>
                <div className="mobile-editor__popup-title">Insert link</div>
                <input
                  className="mobile-editor__popup-input"
                  type="url"
                  inputMode="url"
                  autoComplete="off"
                  placeholder="https://example.com"
                  value={linkUrlInput}
                  onChange={(e) => setLinkUrlInput(e.target.value)}
                />
                <div className="mobile-editor__popup-actions">
                  <button
                    type="button"
                    className="mobile-editor__popup-btn mobile-editor__popup-btn--ghost"
                    onClick={removeLink}
                  >
                    Remove
                  </button>
                  <button type="button" className="mobile-editor__popup-btn" onClick={closePopup}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="mobile-editor__popup-btn mobile-editor__popup-btn--primary"
                    onClick={applyLink}
                  >
                    Add
                  </button>
                </div>
              </>
            )}

            {activePopup === 'imageUrl' && (
              <>
                <div className="mobile-editor__popup-title">Insert image by URL</div>
                <input
                  className="mobile-editor__popup-input"
                  type="url"
                  inputMode="url"
                  autoComplete="off"
                  placeholder="https://example.com/pic.jpg"
                  value={imageUrlInput}
                  onChange={(e) => setImageUrlInput(e.target.value)}
                />
                <div className="mobile-editor__popup-actions">
                  <button type="button" className="mobile-editor__popup-btn" onClick={closePopup}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="mobile-editor__popup-btn mobile-editor__popup-btn--primary"
                    onClick={applyImageUrl}
                  >
                    Add
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


const styles = {
  page: {
    backgroundColor: '#ffffff',
    boxSizing: 'border-box',
    padding: 0,
    margin: 0,
  },
  message: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    color: '#1a1a1a',
    backgroundColor: '#ffffff',
    fontSize: 16,
  },
};
